import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography,
    Checkbox,
    Box,
    Card,
    Grid,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Lock as LockIcon,
    Videocam as VideocamIcon,
    Person as PersonIcon,
    Devices as DevicesIcon,
    ExpandMore as ExpandMoreIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';

import { CampaignMode } from '../../../../types/Campaign';
import { ApplicationDTO } from '../../../../types/Applications';
import { MatchingImportance } from '../../../../types/ApplicationQuestion';
import MatchingImportancePill from '../../../Common/MatchingImportancePill';

interface ApplicationSettingsProps {
    applicationDetails: ApplicationDTO;
    onAvailabilityChange: (includeAvailability: boolean) => void;
    onLocationChange: (includeLocation: boolean) => void;
    onCampaignModeChange: (mode: CampaignMode) => void;
    isReadOnly?: boolean;
}

const ApplicationSettings: React.FC<ApplicationSettingsProps> = ({
    applicationDetails,
    onAvailabilityChange,
    onLocationChange,
    onCampaignModeChange,
    isReadOnly = false
}) => {
    const [expanded, setExpanded] = useState<boolean>(true);

    // Local state derived from applicationDetails
    const [campaignMode, setCampaignMode] = useState<CampaignMode | undefined>(applicationDetails.campaign_mode as CampaignMode);
    const [includeAvailability, setIncludeAvailability] = useState<boolean>(!!applicationDetails.include_availability);
    const [includeLocation, setIncludeLocation] = useState<boolean>(!!applicationDetails.include_location);

    // Sync local state when applicationDetails changes
    useEffect(() => {
        setCampaignMode(applicationDetails.campaign_mode as CampaignMode);
        setIncludeAvailability(!!applicationDetails.include_availability);
        setIncludeLocation(!!applicationDetails.include_location);
    }, [applicationDetails]);

    const handleModeChange = useCallback((mode: CampaignMode) => {
        if (isReadOnly) return;
        setCampaignMode(mode);
        onCampaignModeChange(mode);
    }, [onCampaignModeChange, isReadOnly]);

    const handleAvailabilityChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (isReadOnly) return;
        const checked = event.target.checked;
        setIncludeAvailability(checked);
        onAvailabilityChange(checked);
    }, [onAvailabilityChange, isReadOnly]);

    const handleLocationChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (isReadOnly) return;
        const checked = event.target.checked;
        setIncludeLocation(checked);
        onLocationChange(checked);
    }, [onLocationChange, isReadOnly]);

    return (
        <Accordion
            disableGutters
            defaultExpanded={true}
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                '&:before': {
                    display: 'none',
                },
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)'
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="application-settings-content"
                id="application-settings-header"
                sx={{
                    backgroundColor: 'background.paper',
                    borderBottom: expanded ? '1px solid' : 'none',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon sx={{ mr: 1 }} color="primary" />
                    <Typography variant="h6" component="div">
                        Application Settings
                    </Typography>
                    {isReadOnly && (
                        <Tooltip title="Settings are read-only because the campaign is not in Prep phase">
                            <LockIcon sx={{ ml: 1, fontSize: '1.25rem', color: 'text.secondary' }} />
                        </Tooltip>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3, pb: 2, backgroundColor: 'background.paper' }}>

                {/* Campaign Mode Selection */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                        Campaign Format
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 1 }} justifyContent="center">
                        {[
                            { mode: CampaignMode.IN_PERSON, icon: <PersonIcon fontSize="large" />, desc: 'Face-to-face mentorship sessions' },
                            { mode: CampaignMode.HYBRID, icon: <DevicesIcon fontSize="large" />, desc: 'Combination of in-person and virtual sessions' },
                            { mode: CampaignMode.VIRTUAL, icon: <VideocamIcon fontSize="large" />, desc: 'Fully online mentorship through video calls' }
                        ].map((item) => (
                            <Grid item key={item.mode} xs={12} sm={4}>
                                <Card
                                    onClick={() => handleModeChange(item.mode as CampaignMode)}
                                    sx={{
                                        p: 3,
                                        textAlign: 'center',
                                        cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                        border: campaignMode === item.mode ? 2 : 1,
                                        borderColor: campaignMode === item.mode ? 'primary.main' : 'divider',
                                        backgroundColor: campaignMode === item.mode ? 'primary.lighter' : 'background.paper',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': isReadOnly ? {} : {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
                                        }
                                    }}
                                    elevation={campaignMode === item.mode ? 3 : 1}
                                    variant="outlined"
                                >
                                    <Box sx={{ mb: 1, color: campaignMode === item.mode ? 'primary.main' : 'text.secondary' }}>
                                        {item.icon}
                                    </Box>
                                    <Typography variant="body1" sx={{ fontWeight: campaignMode === item.mode ? 600 : 400, mb: 1 }}>
                                        {item.mode.replace('_', ' ').toLowerCase().replace(/(^|\s)\w/g, char => char.toUpperCase())}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: '90%' }}>
                                        {item.desc}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Required Information Section */}
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                        Required Information
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        {[
                            { key: 'availability', label: 'Availability', checked: includeAvailability, onChange: handleAvailabilityChange, desc: 'Ask applicants to specify times they are available for mentorship sessions', critical: true },
                            { key: 'location', label: 'Location', checked: includeLocation, onChange: handleLocationChange, desc: campaignMode === CampaignMode.VIRTUAL ? 'Ask applicants for their timezone to coordinate virtual sessions' : 'Ask applicants for their physical location for in-person or hybrid sessions', critical: campaignMode !== CampaignMode.VIRTUAL }
                        ].map((item) => (
                            <Grid item key={item.key} xs={12} sm={6}>
                                <Card
                                    sx={{
                                        p: 2.5,
                                        border: item.checked ? 2 : 1,
                                        borderColor: item.checked ? 'primary.main' : 'divider',
                                        backgroundColor: item.checked ? 'primary.lighter' : 'background.paper',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': isReadOnly ? {} : {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
                                        },
                                        cursor: isReadOnly ? 'not-allowed' : 'pointer'
                                    }}
                                    elevation={item.checked ? 3 : 1}
                                    variant="outlined"
                                    onClick={() => !isReadOnly && item.onChange({ target: { checked: !item.checked } } as React.ChangeEvent<HTMLInputElement>)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                        <Checkbox
                                            checked={item.checked}
                                            onChange={item.onChange}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={isReadOnly}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: item.checked ? 600 : 400, lineHeight: 1.2 }}>
                                                {item.label}
                                            </Typography>
                                            {item.critical && (
                                                <Box sx={{ mt: 0.5 }}>
                                                    <MatchingImportancePill 
                                                        importance={MatchingImportance.CRITICAL} 
                                                        size="small" 
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {item.desc}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default ApplicationSettings;